import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import StatusPill from "../common/StatusPill";
import EmptyState from "../common/EmptyState";
import MarketSwitcher from "../common/MarketSwitcher";
import { getBuyerQuotation, createClosedMarketOrder, getClosedMarketOrders, requestNewQuotation } from "../../api/closedMarket";
import { getLocations } from "../../api/locations";
import useConstants from "../../hooks/useConstants";

// Backend's getBuyerClosedMarketOrders only recognizes these three status buckets
// (anything else falls through to "past") — mirrors that grouping instead of an
// arbitrary tab list.
const STATUS_TABS = [
	{ key: "pending", label: "Pending" },
	{ key: "active", label: "Active" },
	{ key: "past", label: "Past" },
];

export default function ClosedMarket() {
	const { constants } = useConstants();
	const [quote, setQuote] = useState(null); // resolved { closedMarketId, quotationId, products: [] } for this buyer
	const [loadingQuote, setLoadingQuote] = useState(true);
	const [status, setStatus] = useState("pending");
	const [orders, setOrders] = useState([]);
	const [loadingOrders, setLoadingOrders] = useState(true);
	const [locations, setLocations] = useState([]);

	// Keyed by productType.id -> { checked, orderSize }. A closed market quote can bundle
	// several products, and the buyer should be able to order more than one of them at once
	// (mirrors ordr-flutter's daily_quotation_screen checkbox-per-product list).
	const [selectedProducts, setSelectedProducts] = useState({});
	const [locationId, setLocationId] = useState("");
	const [deliveryDate, setDeliveryDate] = useState(null);
	const [deliveryTime, setDeliveryTime] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const loadQuote = async () => {
		setLoadingQuote(true);
		const res = await getBuyerQuotation();
		if (res && !res.error && Array.isArray(res.data) && res.data.length > 0) {
			// A buyer belongs to a single closed-market seller group; take the first quotation bundle.
			const entry = res.data[0];
			const quotation = entry.quotationData?.quotation;
			const override = entry.quotationData?.buyerSpecificOverride;
			const products = override ? override.products : quotation?.universalProducts || [];
			setQuote({
				closedMarketId: entry.closedMarketId,
				quotationId: quotation?.id,
				sellerName: entry.seller ? `${entry.seller.firstName} ${entry.seller.lastName}` : "",
				products,
			});
		} else {
			setQuote(null);
		}
		setLoadingQuote(false);
	};

	const loadOrders = async (s) => {
		setLoadingOrders(true);
		const res = await getClosedMarketOrders(s);
		if (res && !res.error) setOrders(res.data?.orders || []);
		else setOrders([]);
		setLoadingOrders(false);
	};

	useEffect(() => {
		loadQuote();
		(async () => {
			const res = await getLocations();
			if (res && !res.error) setLocations(res.data || []);
		})();
	}, []);

	useEffect(() => {
		loadOrders(status);
	}, [status]);

	const toggleProduct = (productId, checked) => {
		setSelectedProducts((prev) => ({
			...prev,
			[productId]: { checked, orderSize: prev[productId]?.orderSize || "" },
		}));
	};

	const setProductOrderSize = (productId, orderSize) => {
		setSelectedProducts((prev) => ({
			...prev,
			[productId]: { checked: prev[productId]?.checked || false, orderSize },
		}));
	};

	const chosenProducts = Object.entries(selectedProducts)
		.filter(([, v]) => v.checked && Number(v.orderSize) > 0)
		.map(([productId, v]) => ({ productId: Number(productId), orderSize: Number(v.orderSize) }));

	const placeOrder = async () => {
		if (!locationId || chosenProducts.length === 0 || !deliveryDate || !deliveryTime) {
			toast.error("Fill in location, at least one product with a size, delivery date and window");
			return;
		}
		setSubmitting(true);
		const res = await createClosedMarketOrder({
			closedMarketId: quote.closedMarketId,
			quotationId: quote.quotationId,
			locationId,
			products: chosenProducts,
			deliveryDate: moment(deliveryDate).format("YYYY-MM-DD"),
			deliveryTime,
		});
		setSubmitting(false);
		if (res && !res.error) {
			toast.success("Closed market order placed");
			setSelectedProducts({});
			setLocationId("");
			setDeliveryDate(null);
			setDeliveryTime("");
			loadOrders(status);
		} else {
			toast.error(res?.msg || "Could not place order");
		}
	};

	const askNewQuote = async () => {
		const res = await requestNewQuotation({ note: "Requesting an updated closed market quote" });
		if (res && !res.error) {
			toast.success("Requested a new quotation");
			loadQuote();
		} else {
			toast.error(res?.msg || "Could not request quotation");
		}
	};

	return (
		<div>
			<MarketSwitcher />
			<PageHeader title="Closed market" subtitle="Lock in today's fixed price without running a live bid" />

			<div className="card p-4 mb-4">
				{loadingQuote ? (
					<Loader small />
				) : quote && quote.products.length > 0 ? (
					<div className="row g-3">
						{quote.sellerName && <div className="col-12 text-muted small">Quote from {quote.sellerName}</div>}
						<div className="col-md-4">
							<label className="form-label">Location</label>
							<select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
								<option value="">Select location</option>
								{locations.map((l) => (
									<option key={l.id} value={l.id}>{l.locationName}</option>
								))}
							</select>
						</div>
						<div className="col-md-4">
							<label className="form-label">Delivery date</label>
							<DatePicker
								className="form-control"
								selected={deliveryDate}
								minDate={new Date()}
								onChange={setDeliveryDate}
								placeholderText="Select date"
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Delivery window</label>
							<select className="form-select" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)}>
								<option value="">Select</option>
								{(constants.deliveryTiming || []).map((opt) => (
									<option key={opt.id} value={opt.value}>{opt.Title}</option>
								))}
							</select>
						</div>

						<div className="col-12">
							<label className="form-label">Products</label>
							<div className="d-flex flex-column gap-2">
								{quote.products.map((p) => {
									const pid = p.productType?.id;
									const row = selectedProducts[pid] || { checked: false, orderSize: "" };
									return (
										<div key={pid} className={`border rounded p-2 ${row.checked ? "border-dark" : ""}`}>
											<div className="d-flex align-items-center gap-2">
												<input
													type="checkbox"
													className="form-check-input"
													checked={row.checked}
													onChange={(e) => toggleProduct(pid, e.target.checked)}
												/>
												<span className="fw-semibold flex-grow-1">{p.productType?.Title}</span>
												<span className="text-muted small">${p.pricePerGallon}/gal</span>
											</div>
											{row.checked && (
												<div className="mt-2">
													<input
														className="form-control"
														style={{ maxWidth: 220 }}
														value={row.orderSize}
														onChange={(e) => setProductOrderSize(pid, e.target.value)}
														placeholder="Order size (gal), e.g. 5000"
													/>
													{p.maxVolume && <div className="text-muted small">Max {p.maxVolume} gal</div>}
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>

						<div className="col-12 d-flex gap-2">
							<button className="btn-accent-solid" disabled={submitting} onClick={placeOrder}>
								{submitting ? "Placing..." : "Place Order"}
							</button>
							<button className="btn-outline" onClick={askNewQuote}>Request New Quote</button>
						</div>
					</div>
				) : (
					<EmptyState title="No active quotation" subtitle="Request today's closed market price to get started." action={<button className="btn-accent-solid" onClick={askNewQuote}>Request Quotation</button>} />
				)}
			</div>

			<div className="d-flex justify-content-between align-items-center mb-3">
				<h6 className="fw-bold mb-0">Closed Market Orders</h6>
			</div>
			<ul className="nav nav-tabs mb-3">
				{STATUS_TABS.map((t) => (
					<li className="nav-item" key={t.key}>
						<button className={`nav-link ${status === t.key ? "active" : ""}`} onClick={() => setStatus(t.key)}>
							{t.label}
						</button>
					</li>
				))}
			</ul>

			{loadingOrders ? (
				<Loader />
			) : orders.length === 0 ? (
				<EmptyState title="No orders in this status" />
			) : (
				<div className="card">
					<table className="table align-middle mb-0">
						<thead>
							<tr className="text-muted small">
								<th className="ps-3">Order</th>
								<th>Product</th>
								<th>Size</th>
								<th>Delivery</th>
								<th>Status</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{orders.map((o) => (
								<tr key={o._id || o.id}>
									<td className="ps-3">{o.orderId || (o._id || o.id || "").slice(-6).toUpperCase()}</td>
									<td>{(o.productType || []).map((p) => p.Title).join(", ") || "-"}</td>
									<td>{o.orderSize ? `${o.orderSize} gal` : "-"}</td>
									<td>{o.deliveryDate ? moment(o.deliveryDate).format("MMM D, YYYY") : "-"}</td>
									<td>
										<StatusPill order={o} />
									</td>
									<td>
										{/* The backend's order-detail lookup matches on the bidRequest's custom `id` field,
									    not Mongo's `_id` — linking with `_id` 404s with "Order not found". */}
									<Link className="btn btn-sm btn-outline-secondary" to={`/closed-market/${o.id || o._id}`}>View</Link>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
